import { type ComponentProps, memo, useEffect, useRef } from 'react';
import { useCountUp } from 'react-countup';
import { usePrevious } from 'react-use';

import { cn } from '@air/utils-tailwind';
import { formatVideoTimecodeValue } from '@air/utils-video';

const formattingFn = (value: number) => formatVideoTimecodeValue({ value });

export type PlayerTimeFormatTimecodeFramesProps = ComponentProps<'span'> & {
  frames: number;
};

export const PlayerTimeFormatTimecodeFrames = memo(
  ({ className, frames, ...restOfProps }: PlayerTimeFormatTimecodeFramesProps) => {
    const countRef = useRef<HTMLSpanElement>(null);
    const { reset, update } = useCountUp({
      duration: 1,
      end: 0,
      formattingFn,
      ref: countRef,
      start: 0,
    });
    const prevFrames = usePrevious(frames);

    useEffect(() => {
      if (frames !== undefined && prevFrames !== undefined && frames > prevFrames) {
        update(frames);
      } else if (frames) {
        /**
         * When the next value is lower than the previous value, we need to reset the count up before
         * updating the value to prevent the count up from counting down.
         */
        reset();
        update(frames);
      }
    }, [frames, prevFrames, reset, update]);

    return (
      <span
        className={cn(className)}
        data-testid="PLAYER_TIME_FORMAT_TIMECODE_FRAMES"
        {...restOfProps}
        ref={countRef}
      />
    );
  },
);

PlayerTimeFormatTimecodeFrames.displayName = 'PlayerTimeFormatTimecodeFrames';
